import chunk from 'lodash/chunk';
import { createAsyncThunk, createEntityAdapter, createSelector, createSlice, EntityState } from '@reduxjs/toolkit';
import { LatestKsSession } from '../models/latestKsSession';
import { Operator, Query } from '../models/query';
import { exceptionOf } from '../models';
import { KsSessionService } from '../services';
import { AppState } from '.';

export const LATEST_KS_SESSION_FEATURE_KEY = 'latest-ks-session';
const MAX_NUMBER_OF_RECORDS = 50;

type LatestKsSessionEntity = LatestKsSession;
const latestKsSessionAdapter = createEntityAdapter<LatestKsSessionEntity>({
  selectId: e => e.userId,
});

interface LatestKsSessionState extends EntityState<LatestKsSessionEntity> {
  loading: boolean;
  error?: string;
}

export const createInitialState = (): LatestKsSessionState => (
  latestKsSessionAdapter.getInitialState({
    loading: false,
  })
);

export const doGetLatestKsSessionByOrgIdAndUserId = createAsyncThunk(
  'latestKsSession/getLatestKsSessionByOrgIdAndUserId',
  async (input: { organizationId: LatestKsSession['organizationId'], userIds: LatestKsSession['userId'][] }, { rejectWithValue }) => {
    try {
      const chunks = chunk(input.userIds, MAX_NUMBER_OF_RECORDS);

      const latestKsSessions: LatestKsSession[] = [];
      // eslint-disable-next-line
      for (const chunk of chunks) {
        const criteria: Array<Query<LatestKsSession>> = [
          { field: 'organizationId', operator: Operator.EQ, value: input.organizationId },
          { field: 'userId', operator: Operator.IN, value: chunk },
        ];

        // eslint-disable-next-line
        const latestKsSession = await KsSessionService.getLatestSessions(...criteria);
        latestKsSessions.push(...latestKsSession.data);
      }

      return latestKsSessions;
    } catch (e: any) {
      return rejectWithValue(exceptionOf(e).toJson());
    }
  },
);

const latestKsSessionSlice = createSlice({
  name: LATEST_KS_SESSION_FEATURE_KEY,
  initialState: createInitialState(),
  reducers: {},
  extraReducers: builder => {
    builder.addCase(doGetLatestKsSessionByOrgIdAndUserId.pending, state => {
      state.loading = true;
    });
    builder.addCase(doGetLatestKsSessionByOrgIdAndUserId.fulfilled, (state, action) => {
      state.loading = false;
      latestKsSessionAdapter.setAll(state, action.payload);
    });
    builder.addCase(doGetLatestKsSessionByOrgIdAndUserId.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });
  },
});

const selectLatestKsSession = (
  state: AppState,
) => state[LATEST_KS_SESSION_FEATURE_KEY];

const { selectAll } = latestKsSessionAdapter.getSelectors(selectLatestKsSession);

export const selectLatestKsSessionByOrganizationId = () => createSelector(
  selectAll,
  state => state,
);

export default latestKsSessionSlice.reducer;
